import { useState, useContext, useEffect } from "react";
import {
  Typography,
  Container,
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  Stack,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

import { UserContext } from "../context/UserContext";
import { Link, useNavigate } from "react-router-dom";

const Profile = () => {
  const { userState, dispatch } = useContext(UserContext); // Access global user state and dispatch function
  const [isEditing, setIsEditing] = useState(false); // State to track if the user is editing their profile

  const navigate = useNavigate();

  useEffect(() => {
    if (!userState?.username) {
      navigate("/login"); // Redirect to login if the user is not logged in
    }
  }, [userState, navigate]);

  // Local state for editing user information
  const [fname, setFname] = useState(userState.fname);
  const [lname, setLname] = useState(userState.lname);
  const [email, setEmail] = useState(userState.email);

  // Enable edit mode
  const handleEditClick = () => setIsEditing(true);

  // Cancel edit mode and restore original values
  const handleCancelClick = () => {
    setFname(userState.fname);
    setLname(userState.lname);
    setEmail(userState.email);
    setIsEditing(false);
  };

  // Save edited user information and update global state
  const handleSave = () => {
    dispatch({
      type: "editUserInfo",
      payload: { fname, lname, email },
    });
    setIsEditing(false);
  };

  // Function to calculate total purchased items and total amount spent
  const calculateTotal = (products) => {
    let amount = 0;
    let total = 0;
    products.forEach((p) => {
      amount += p.amount;
      total += p.amount * p.price;
    });
    return { amount, total };
  };

  const { amount, total } = calculateTotal(userState.purchasedProducts);

  return (
    <Container
      sx={{
        marginTop: "100px",
        marginBottom: "30px",
        width: { xs: "90%", sm: "80%", md: "70%", lg: "60%" },
      }}
    >
      <Box sx={{ color: "white" }}>
        {/* User Information Section */}
        <Box
          sx={{
            border: "2px solid #C9A227",
            borderRadius: "15px",
            padding: "12px",
            position: "relative",
          }}
        >
          <Typography
            variant="h5"
            sx={{ color: "#C9A227", fontWeight: "bold", mb: "5px" }}
          >
            User's Info
          </Typography>

          {isEditing ? (
            <>
              {/* Editable Input Fields */}
              <Stack
                sx={{ display: "flex", flexDirection: "row", gap: "10px" }}
              >
                <TextField
                  margin="normal"
                  label="First Name"
                  name="fname"
                  value={fname}
                  sx={{
                    width: "50%",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "gray",
                        borderWidth: "2px",
                      },
                    },
                  }}
                  onChange={(e) => setFname(e.target.value)}
                />
                <TextField
                  margin="normal"
                  label="Last Name"
                  name="lname"
                  value={lname}
                  sx={{
                    width: "50%",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "gray",
                        borderWidth: "2px",
                      },
                    },
                  }}
                  onChange={(e) => setLname(e.target.value)}
                />
              </Stack>
              <TextField
                fullWidth
                margin="normal"
                label="Email"
                name="email"
                value={email}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "gray",
                      borderWidth: "2px",
                    },
                  },
                }}
                onChange={(e) => setEmail(e.target.value)}
              />

              {/* Save & Cancel Buttons */}
              <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                >
                  Save
                </Button>
                <Button
                  variant="contained"
                  sx={{ backgroundColor: "#3A3F4B", color: "white" }}
                  onClick={handleCancelClick}
                >
                  Cancel
                </Button>
              </Box>
            </>
          ) : (
            <>
              {/* Display User Info */}
              <Typography variant="body1">
                Name: {userState.fname} {userState.lname}
              </Typography>
              <Typography variant="body1">Email: {userState.email}</Typography>
              <Typography variant="body1">
                Username: {userState.username}
              </Typography>

              {/* Edit Button */}
              <IconButton
                sx={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  color: "#C9A227",
                }}
                onClick={handleEditClick}
              >
                <EditIcon />
              </IconButton>
            </>
          )}
        </Box>
      </Box>

      {/* Purchase History Section */}
      {userState.purchasedProducts.length > 0 ? (
        <Box
          sx={{
            color: "white",
            mt: "10px",
            border: "2px solid #C9A227",
            borderRadius: "15px",
            padding: "12px",
          }}
        >
          <Typography
            variant="h5"
            sx={{ color: "#C9A227", fontWeight: "bold", mb: "5px" }}
          >
            Purchase Log
          </Typography>
          <Typography variant="body1">
            Total Items Purchased: {amount}
          </Typography>
          <Typography variant="body1" sx={{ mb: "20px" }}>
            Total Spent: ${total.toFixed(2)}
          </Typography>

          {/* List Purchased Products */}
          {userState.purchasedProducts.map((p, index) => (
            <Card
              key={index}
              sx={{ backgroundColor: "#23262D", color: "white", mb: 2 }}
            >
              <CardContent>
                <Link
                  to={`/product/${p.id}`}
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <Typography variant="body1"> {p.title}</Typography>
                </Link>
                <Typography variant="body2" sx={{ color: "#A5AAB5" }}>
                  {p.amount} items
                </Typography>
                <Typography variant="body2" sx={{ color: "#A5AAB5" }}>
                  ${p.price * p.amount}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        // No purchases message
        <Box
          sx={{
            color: "white",
            mt: "10px",
            border: "2px solid #C9A227",
            borderRadius: "15px",
            padding: "12px",
          }}
        >
          <Typography
            variant="h5"
            sx={{ color: "gray", mt: "20px", mb: "20px" }}
          >
            You haven't purchased any products yet.
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default Profile; // Export Profile component
